import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideX } from '@ng-icons/lucide';
import { AutocompleteComponent } from '@mm-aggregator/drf-dynamics';

import {
  FinancialEntityBalanceService,
  FundMerchantRequest,
  ReconcileRequest,
  TopupRequest,
} from '../../services/financial-entity-balance.service';
import {
  FundingSource,
  MerchantFundingService,
  TopupScope,
  WalletFundRequest,
} from '../../services/merchant-funding.service';
import { UI } from '../ui';
import { formatHumanAmount } from '../../utils/admin-formatting';
import {
  fundingSourceLabel,
  scopeNeedsWallet,
  scopesForMode,
  topupScopeHint,
  topupScopeLabel,
} from '../../utils/topup-labels';

export type TopupWizardMode = 'fund-merchant' | 'wallet-fund' | 'integration-topup' | 'reconcile';

@Component({
  selector: 'app-topup-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIcon, AutocompleteComponent, ...UI],
  providers: [provideIcons({ lucideCheck, lucideX })],
  templateUrl: './topup-wizard.component.html',
})
export class TopupWizardComponent implements OnInit {
  @Input({ required: true }) mode!: TopupWizardMode;
  @Input({ required: true }) marketCurrencyCode = '';
  @Input() marketCurrencyId = '';
  @Input() displayCurrencyCode = '';
  @Input() countryCode = '';
  @Input() marketDisplayLabel = '';
  @Input() countryName = '';
  @Input() entityId?: string;
  @Input() walletId?: string;
  @Input() supportsBalanceApi = false;
  @Input() ledgerBalance?: string;

  @Output() completed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly balanceService = inject(FinancialEntityBalanceService);
  private readonly fundingService = inject(MerchantFundingService);

  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly step = signal(1);

  selectedScope: TopupScope = 'INTEGRATION_ONLY';
  fundingSource: FundingSource = 'OPERATOR_DIRECT';
  amount = '';
  reference = '';
  notes = '';
  toppedUpAt = '';
  reconcileBalance = '';
  reconcileSource: 'manual' | 'fetch' = 'manual';

  walletIdValue = '';
  walletDisplay = '';
  walletCountryDisplay = '';
  financialEntityId = '';
  financialEntityDisplay = '';

  readonly fundingSources: FundingSource[] = ['OPERATOR_DIRECT', 'BANK_WIRE', 'INTERNAL_TRANSFER'];

  get totalSteps(): number {
    if (this.mode === 'integration-topup') return 2;
    if (this.mode === 'reconcile') return 3;
    return 3;
  }

  get stepTitles(): string[] {
    if (this.mode === 'reconcile') {
      return [
        $localize`:@@topup.wizard.step.source:Source`,
        $localize`:@@topup.wizard.step.snapshot:Snapshot`,
        $localize`:@@topup.wizard.step.review:Review`,
      ];
    }
    if (this.mode === 'integration-topup') {
      return [
        $localize`:@@topup.wizard.step.details:Details`,
        $localize`:@@topup.wizard.step.review:Review`,
      ];
    }
    return [
      $localize`:@@topup.wizard.step.scope:Scope`,
      $localize`:@@topup.wizard.step.details:Details`,
      $localize`:@@topup.wizard.step.review:Review`,
    ];
  }

  get availableScopes(): TopupScope[] {
    if (this.mode === 'wallet-fund') {
      return ['WALLET_ONLY', 'WALLET_AND_INTEGRATION'];
    }
    return scopesForMode(this.mode === 'integration-topup' ? 'integration-topup' : 'fund-merchant');
  }

  get progressPercent(): number {
    return Math.round((this.step() / this.totalSteps) * 100);
  }

  get currencyCode(): string {
    return this.displayCurrencyCode || this.marketCurrencyCode.split('_')[0] || '';
  }

  get resolvedCountryCode(): string {
    if (this.countryCode) return this.countryCode;
    const parts = this.marketCurrencyCode.split('_');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  get marketCountryLabel(): string {
    if (this.countryName) return this.countryName;
    if (this.marketDisplayLabel) return this.marketDisplayLabel;
    const code = this.resolvedCountryCode;
    return code || '';
  }

  get walletSearchParams(): Record<string, string> {
    const code = this.resolvedCountryCode;
    return code ? { country__code: code } : {};
  }

  ngOnInit(): void {
    if (this.mode === 'integration-topup') {
      this.selectedScope = 'INTEGRATION_ONLY';
      this.fundingSource = 'BANK_WIRE';
    } else if (this.mode === 'wallet-fund') {
      this.selectedScope = 'WALLET_AND_INTEGRATION';
      this.fundingSource = 'OPERATOR_DIRECT';
      if (this.walletId) {
        this.walletIdValue = this.walletId;
      }
    }
  }

  scopeLabel(scope: TopupScope): string {
    return topupScopeLabel(scope);
  }

  scopeHint(scope: TopupScope): string {
    return topupScopeHint(scope);
  }

  fundingLabel(source: FundingSource): string {
    return fundingSourceLabel(source);
  }

  needsWallet(): boolean {
    return scopeNeedsWallet(this.selectedScope);
  }

  selectScope(scope: TopupScope): void {
    this.selectedScope = scope;
    if (scope === 'WALLET_ONLY') {
      this.fundingSource = 'BANK_WIRE';
    }
  }

  close(): void {
    this.cancelled.emit();
  }

  prevStep(): void {
    if (this.step() > 1) {
      this.step.update((s) => s - 1);
    }
  }

  nextStep(): void {
    this.error.set(null);
    if (!this.validateCurrentStep()) return;
    if (this.step() < this.totalSteps) {
      this.step.update((s) => s + 1);
    }
  }

  validateCurrentStep(): boolean {
    const current = this.step();
    if (this.mode === 'reconcile') {
      if (current === 2 && this.reconcileSource === 'manual' && !String(this.reconcileBalance ?? '').trim()) {
        this.error.set($localize`:@@topup.error.reconcileBalance:Enter the provider-reported balance.`);
        return false;
      }
      return true;
    }
    const detailsStep = this.mode === 'integration-topup' ? 1 : 2;
    if (current === detailsStep) {
      if (!String(this.amount ?? '').trim() || Number(this.amount) <= 0) {
        this.error.set($localize`:@@topup.error.amount:Enter a positive amount.`);
        return false;
      }
      if (this.needsWallet() && !this.walletIdValue && this.mode !== 'wallet-fund') {
        this.error.set($localize`:@@topup.error.wallet:Select a merchant wallet.`);
        return false;
      }
      if (this.mode === 'wallet-fund' && this.selectedScope === 'WALLET_AND_INTEGRATION' && !this.financialEntityId) {
        this.error.set($localize`:@@topup.error.entity:Select a financial entity for operator deposit.`);
        return false;
      }
    }
    return true;
  }

  formatAmount(value: string): string {
    return formatHumanAmount(value, this.currencyCode);
  }

  selectWallet(item: Record<string, unknown>): void {
    this.walletIdValue = String(item['id'] ?? '');
    this.walletDisplay = String(item['label'] ?? item['code'] ?? this.walletIdValue);
    this.walletCountryDisplay = String(item['lookup_subtitle'] ?? '');
  }

  clearWallet(): void {
    this.walletIdValue = '';
    this.walletDisplay = '';
    this.walletCountryDisplay = '';
  }

  selectEntity(item: Record<string, unknown>): void {
    this.financialEntityId = String(item['id'] ?? '');
    this.financialEntityDisplay = String(item['label'] ?? item['code'] ?? this.financialEntityId);
  }

  clearEntity(): void {
    this.financialEntityId = '';
    this.financialEntityDisplay = '';
  }

  submit(): void {
    if (!this.validateCurrentStep()) return;
    this.saving.set(true);
    this.error.set(null);
    const toppedUpAt = this.toppedUpAt ? new Date(this.toppedUpAt).toISOString() : new Date().toISOString();

    if (this.mode === 'reconcile') {
      this.submitReconcile(toppedUpAt);
      return;
    }

    if (this.mode === 'integration-topup') {
      this.submitIntegrationTopup(toppedUpAt);
      return;
    }

    if (this.mode === 'wallet-fund' && this.walletId) {
      if (!this.marketCurrencyId) {
        this.error.set($localize`:@@topup.error.marketCurrency:Market currency could not be resolved.`);
        this.saving.set(false);
        return;
      }
      this.submitWalletFund(toppedUpAt);
      return;
    }

    if (this.mode === 'fund-merchant' && this.entityId) {
      if (!this.marketCurrencyId) {
        this.error.set($localize`:@@topup.error.marketCurrency:Market currency could not be resolved.`);
        this.saving.set(false);
        return;
      }
      this.submitFundMerchant(toppedUpAt);
      return;
    }

    this.error.set($localize`:@@topup.error.config:Wizard is not configured correctly.`);
    this.saving.set(false);
  }

  private submitFundMerchant(toppedUpAt: string): void {
    const body: FundMerchantRequest = {
      amount: this.amount,
      topped_up_at: toppedUpAt,
      topup_scope: this.selectedScope,
      funding_source: this.fundingSource,
      market_currency_code: this.marketCurrencyCode,
      reference: this.reference,
      notes: this.notes,
    };
    if (this.marketCurrencyId) {
      body.market_currency = this.marketCurrencyId;
    }
    if (this.needsWallet() && this.walletIdValue) {
      body.wallet = this.walletIdValue;
    }
    this.balanceService.fundMerchant(this.entityId!, body).subscribe({
      next: () => {
        this.saving.set(false);
        this.completed.emit();
      },
      error: (err) => {
        this.error.set(err?.error?.detail || $localize`:@@topup.error.fundFailed:Failed to record funding.`);
        this.saving.set(false);
      },
    });
  }

  private submitWalletFund(toppedUpAt: string): void {
    const body: WalletFundRequest = {
      amount: this.amount,
      topped_up_at: toppedUpAt,
      topup_scope: this.selectedScope,
      funding_source: this.fundingSource,
      market_currency_code: this.marketCurrencyCode,
      reference: this.reference,
      notes: this.notes,
    };
    if (this.marketCurrencyId) {
      body.market_currency = this.marketCurrencyId;
    }
    if (this.financialEntityId) {
      body.financial_entity = this.financialEntityId;
    }
    this.fundingService.fundWallet(this.walletId!, body).subscribe({
      next: () => {
        this.saving.set(false);
        this.completed.emit();
      },
      error: (err) => {
        this.error.set(err?.error?.detail || $localize`:@@topup.error.fundFailed:Failed to record funding.`);
        this.saving.set(false);
      },
    });
  }

  private submitIntegrationTopup(toppedUpAt: string): void {
    const body: TopupRequest = {
      amount: this.amount,
      topped_up_at: toppedUpAt,
      market_currency_code: this.marketCurrencyCode,
      reference: this.reference,
      notes: this.notes,
    };
    if (this.currencyCode) {
      body.currency = this.currencyCode;
    }
    this.balanceService.createTopup(this.entityId!, body).subscribe({
      next: () => {
        this.saving.set(false);
        this.completed.emit();
      },
      error: (err) => {
        this.error.set(err?.error?.detail || $localize`:@@topup.error.topupFailed:Failed to record topup.`);
        this.saving.set(false);
      },
    });
  }

  private submitReconcile(_toppedUpAt: string): void {
    if (!this.entityId) {
      this.error.set($localize`:@@topup.error.entityMissing:Entity is required for reconciliation.`);
      this.saving.set(false);
      return;
    }
    if (this.reconcileSource === 'fetch') {
      this.balanceService
        .fetchBalance(this.entityId, {
          market_currency_code: this.marketCurrencyCode,
          currency: this.currencyCode || undefined,
          notes: this.notes,
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.completed.emit();
          },
          error: (err) => {
            this.error.set(err?.error?.detail || $localize`:@@topup.error.fetchFailed:Failed to fetch balance.`);
            this.saving.set(false);
          },
        });
      return;
    }
    const body: ReconcileRequest = {
      provider_reported_balance: this.reconcileBalance,
      market_currency_code: this.marketCurrencyCode,
      currency: this.currencyCode || undefined,
      notes: this.notes,
    };
    this.balanceService.reconcile(this.entityId, body).subscribe({
      next: () => {
        this.saving.set(false);
        this.completed.emit();
      },
      error: (err) => {
        this.error.set(err?.error?.detail || $localize`:@@topup.error.reconcileFailed:Failed to save reconciliation.`);
        this.saving.set(false);
      },
    });
  }
}
